import {useState, type FormEvent} from 'react';
import {AwButton, AwErrorState, AwField} from '@ascension-tracker/ascension-ui';
import {workflowStates} from '@ascension-tracker/shared-types';
import {ApiClientError} from '../api.js';
import {useDialogs} from './DialogProvider.js';
import {SectionGraph} from './SectionGraph.js';
import {useCreateSection, useCreateSectionTransition, useDeleteSection, useDeleteSectionTransition, useProjectSectionTransitions, useProjectSections, useRenameSection, useReorderSection, useUpdateSectionWorkflowState, type Section} from '../hooks/useTasks.js';

const workflowStateLabels: Record<Section['workflowState'], string> = {todo: 'To Do', in_progress: 'In Progress', done: 'Done'};

export function SectionManager({projectId}: {projectId: string}) {
  const {confirm} = useDialogs();
  const sections = useProjectSections(projectId);
  const transitions = useProjectSectionTransitions(projectId);
  const createSection = useCreateSection(projectId);
  const renameSection = useRenameSection(projectId);
  const updateWorkflowState = useUpdateSectionWorkflowState(projectId);
  const reorderSection = useReorderSection(projectId);
  const deleteSection = useDeleteSection(projectId);
  const createTransition = useCreateSectionTransition(projectId);
  const deleteTransition = useDeleteSectionTransition(projectId);
  const [name, setName] = useState('');
  const [workflowState, setWorkflowState] = useState<Section['workflowState']>('in_progress');
  const [error, setError] = useState('');

  const ordered = [...(sections.data ?? [])].sort((a, b) => a.position - b.position);
  const workflowSections = ordered.filter(section => !section.isBacklog);
  const backlogSection = ordered.find(section => section.isBacklog);

  function runOrReport(action: () => Promise<unknown>) {
    setError('');
    action().catch(e => setError(e instanceof ApiClientError ? e.message : 'That action failed.'));
  }

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!name.trim()) return;
    setError('');
    try {
      await createSection.mutateAsync({name: name.trim(), workflowState});
      setName('');
      setWorkflowState('in_progress');
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to create section');
    }
  }

  function reorderTo(sectionId: string, newIndex: number) {
    const rest = workflowSections.filter(section => section.id !== sectionId);
    const before = rest[newIndex - 1] ?? null;
    const after = rest[newIndex] ?? null;
    runOrReport(() => reorderSection.mutateAsync({sectionId, beforeSectionId: before?.id ?? null, afterSectionId: after?.id ?? null}));
  }
  function moveUp(sectionId: string) {
    const index = workflowSections.findIndex(section => section.id === sectionId);
    if (index > 0) reorderTo(sectionId, index - 1);
  }
  function moveDown(sectionId: string) {
    const index = workflowSections.findIndex(section => section.id === sectionId);
    if (index !== -1 && index < workflowSections.length - 1) reorderTo(sectionId, index + 1);
  }

  async function removeSection(section: Section) {
    const ok = await confirm(`Delete "${section.name}"? Tasks in it become unsectioned.`, {confirmLabel: 'Delete Section', danger: true});
    if (!ok) return;
    runOrReport(() => deleteSection.mutateAsync(section.id));
  }

  return (
    <div className="section-manager">
      <p className="task-detail__muted">
        Reorder sections to match your flow, then drag from the small circle on a section onto another section below to allow tasks to move directly between them — each direction is separate, so add the arrow back the other way too if tasks should be able to return. The Backlog is exempt from this graph — it's a holding pool outside the workflow. Moving a task into a "Done" section completes it automatically; moving it back out reopens it.
      </p>
      {error && <AwErrorState>{error}</AwErrorState>}
      <ol className="section-manager__list">
        {workflowSections.map((section, index) => (
          <li key={section.id} className="section-manager__row">
            <div className="section-manager__order">
              <button type="button" aria-label={`Move ${section.name} earlier`} disabled={index === 0} onClick={() => moveUp(section.id)}>↑</button>
              <button type="button" aria-label={`Move ${section.name} later`} disabled={index === workflowSections.length - 1} onClick={() => moveDown(section.id)}>↓</button>
            </div>
            <SectionNameField section={section} onRename={value => runOrReport(() => renameSection.mutateAsync({sectionId: section.id, name: value}))}/>
            <select
              aria-label={`Workflow stage for ${section.name}`}
              className={`task-section__workflow-select task-section__workflow-select--${section.workflowState}`}
              value={section.workflowState}
              onChange={event => runOrReport(() => updateWorkflowState.mutateAsync({sectionId: section.id, workflowState: event.target.value as Section['workflowState']}))}
            >
              {workflowStates.map(state => <option key={state} value={state}>{workflowStateLabels[state]}</option>)}
            </select>
            {section.isDefault && <span className="section-manager__badge">Default</span>}
            <AwButton type="button" variant="danger" disabled={section.isDefault} onClick={() => { void removeSection(section); }}>Delete</AwButton>
          </li>
        ))}
      </ol>

      <form className="task-quick-add" onSubmit={create}>
        <AwField id="settings-new-section-name" label="" placeholder="New section name…" value={name} onChange={event => setName(event.target.value)}/>
        <select aria-label="Workflow stage" className={`task-section__workflow-select task-section__workflow-select--${workflowState}`} value={workflowState} onChange={event => setWorkflowState(event.target.value as Section['workflowState'])}>
          {workflowStates.map(state => <option key={state} value={state}>{workflowStateLabels[state]}</option>)}
        </select>
        <AwButton type="submit" variant="secondary" disabled={!name.trim()}>Add section</AwButton>
      </form>

      {workflowSections.length > 1 && (
        <SectionGraph
          sections={workflowSections}
          transitions={transitions.data ?? []}
          onCreateTransition={(fromSectionId, toSectionId) => runOrReport(() => createTransition.mutateAsync({fromSectionId, toSectionId}))}
          onDeleteTransition={(fromSectionId, toSectionId) => runOrReport(() => deleteTransition.mutateAsync({fromSectionId, toSectionId}))}
        />
      )}

      {backlogSection && (
        <div className="section-manager__backlog">
          <i className="task-section__dot" aria-hidden="true"/>
          <SectionNameField section={backlogSection} onRename={value => runOrReport(() => renameSection.mutateAsync({sectionId: backlogSection.id, name: value}))}/>
          <span className="task-section__badge">Backlog — not part of the workflow sequence</span>
        </div>
      )}
    </div>
  );
}

function SectionNameField({section, onRename}: {section: Section; onRename: (name: string) => void}) {
  const [value, setValue] = useState(section.name);
  return (
    <AwField
      id={`section-manager-name-${section.id}`}
      label=""
      value={value}
      onChange={event => setValue(event.target.value)}
      onBlur={() => { const trimmed = value.trim(); if (trimmed && trimmed !== section.name) onRename(trimmed); else setValue(section.name); }}
    />
  );
}
